import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ChevronRight, Mail, Twitter, Linkedin, Globe, 
  Calendar, FileText, Eye, BookOpen
} from 'lucide-react';
import ArticleCard from '../components/news/ArticleCard';
import { authorService } from '../services/api';
import styles from './AuthorPage.module.css';

export default function AuthorPage() {
  const { id } = useParams();
  
  const [author, setAuthor] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const ARTICLES_PER_PAGE = 9;

  useEffect(() => {
    loadAuthor();
  }, [id]);

  useEffect(() => {
    if (author) {
      loadArticles();
    }
  }, [author, page]);

  const loadAuthor = async () => {
    try {
      setLoading(true);
      const data = await authorService.getById(id);
      setAuthor(data);
      setPage(1);
      setArticles([]);
    } catch (error) {
      console.error('Error loading author:', error);
    }
  };

  const loadArticles = async () => {
    try {
      const newArticles = await authorService.getArticles(id, {
        page,
        limit: ARTICLES_PER_PAGE
      });
      
      if (page === 1) {
        setArticles(newArticles);
      } else {
        setArticles(prev => [...prev, ...newArticles]);
      }
      
      setHasMore(newArticles.length === ARTICLES_PER_PAGE);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    setPage(p => p + 1);
  };

  // Estadísticas del autor
  const totalViews = articles.reduce((sum, a) => sum + (a.views || 0), 0);
  const totalArticles = author?.articlesCount || articles.length;

  if (loading && !author) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>Cargando perfil...</p>
      </div>
    );
  }

  if (!author) {
    return (
      <div className={styles.notFound}>
        <h1>Autor no encontrado</h1>
        <p>El autor que buscas no existe.</p>
        <Link to="/" className="btn btn-primary">Volver al inicio</Link>
      </div>
    );
  }

  return (
    <main className={styles.authorPage}>
      {/* Breadcrumb */}
      <nav className={styles.breadcrumb}>
        <div className="container">
          <Link to="/">Inicio</Link>
          <ChevronRight size={14} />
          <span>Autor</span>
          <ChevronRight size={14} />
          <span>{author.name}</span>
        </div>
      </nav>

      {/* Header del autor */}
      <header className={styles.header}>
        <div className="container">
          <div className={styles.headerContent}>
            {/* Avatar */}
            <div className={styles.avatarWrapper}>
              {author.avatar ? (
                <img 
                  src={author.avatar} 
                  alt={author.name}
                  className={styles.avatar}
                />
              ) : (
                <div className={styles.avatarPlaceholder}>
                  {author.name.charAt(0)}
                </div>
              )}
            </div>

            {/* Info */}
            <div className={styles.info}>
              <h1 className={styles.name}>{author.name}</h1>
              {author.role && (
                <p className={styles.role}>{author.role}</p>
              )}
              {author.bio && (
                <p className={styles.bio}>{author.bio}</p>
              )}

              {/* Estadísticas */}
              <div className={styles.stats}>
                <div className={styles.stat}>
                  <FileText size={18} />
                  <span><strong>{totalArticles}</strong> artículos</span>
                </div>
                <div className={styles.stat}>
                  <Eye size={18} />
                  <span><strong>{totalViews.toLocaleString()}</strong> lecturas</span>
                </div>
                {author.joinedAt && (
                  <div className={styles.stat}>
                    <Calendar size={18} />
                    <span>Desde {new Date(author.joinedAt).getFullYear()}</span>
                  </div>
                )}
              </div>

              {/* Redes sociales */}
              <div className={styles.social}>
                {author.email && (
                  <a 
                    href={`mailto:${author.email}`}
                    className={styles.socialLink}
                    title="Email"
                  >
                    <Mail size={20} />
                  </a>
                )}
                {author.twitter && (
                  <a 
                    href={`https://twitter.com/${author.twitter}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialLink}
                    title="Twitter"
                  >
                    <Twitter size={20} />
                  </a>
                )}
                {author.linkedin && (
                  <a 
                    href={author.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialLink}
                    title="LinkedIn"
                  >
                    <Linkedin size={20} />
                  </a>
                )}
                {author.website && (
                  <a 
                    href={author.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialLink}
                    title="Sitio web"
                  >
                    <Globe size={20} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Artículos del autor */}
      <section className={styles.articles}>
        <div className="container">
          <h2 className={styles.articlesTitle}>
            <BookOpen size={24} />
            Artículos de {author.name.split(' ')[0]}
          </h2>

          {articles.length === 0 && !loading ? (
            <div className={styles.empty}>
              <p>Este autor aún no tiene artículos publicados</p>
            </div>
          ) : (
            <>
              <div className={styles.articlesGrid}>
                {articles.map(article => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    variant="default"
                    showExcerpt={true}
                    showAuthor={false}
                  />
                ))}
              </div>

              {hasMore && (
                <div className={styles.loadMore}>
                  <button 
                    className="btn btn-outline"
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? 'Cargando...' : 'Cargar más artículos'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Artículos destacados */}
      {articles.length > 3 && (
        <section className={styles.featured}>
          <div className="container">
            <h2 className={styles.featuredTitle}>Lo más leído de {author.name.split(' ')[0]}</h2>
            <div className={styles.featuredList}>
              {[...articles]
                .sort((a, b) => (b.views || 0) - (a.views || 0))
                .slice(0, 5)
                .map((article, index) => (
                  <Link
                    key={article.id}
                    to={`/articulo/${article.slug}`}
                    className={styles.featuredItem}
                  >
                    <span className={styles.featuredNumber}>{index + 1}</span>
                    <div className={styles.featuredContent}>
                      <span className={styles.featuredCategory}>
                        {article.category?.name}
                      </span>
                      <h3>{article.title}</h3>
                      <span className={styles.featuredViews}>
                        <Eye size={14} />
                        {article.views?.toLocaleString()} lecturas
                      </span>
                    </div>
                  </Link>
                ))
              }
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
